import {
    AfterViewInit,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    OnDestroy,
    OnInit,
    signal,
    viewChild
} from '@angular/core';
import { OpportunityView } from '@api-proxies';
import { OpportunityDashboardService } from '@http/clients/opportunity-dashboard.service';
import { ClientSideGridComponent } from '@shared/grids/client-side-grid/client-side-grid.component';
import { SectionHeaderComponent } from '@shared/section-header/section-header.component';
import { ColDef } from 'ag-grid-enterprise';
import { createDashboardOpportunitiesGridColumnDefs } from './dashboard-opportunities-grid.config';

type DashboardOpportunitiesGrid = 'newProjects' | 'upcomingBids';

@Component({
    selector: 'app-dashboard-opportunities-grid',
    templateUrl: './dashboard-opportunities-grid.component.html',
    styleUrls: ['./dashboard-opportunities-grid.component.scss'],
    imports: [SectionHeaderComponent, ClientSideGridComponent]
})
export class DashboardOpportunitiesGridComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly opportunityDashboardService = inject(OpportunityDashboardService);
    private readonly grid = viewChild(ClientSideGridComponent);
    private readonly gridHost = viewChild<ElementRef<HTMLElement>>('gridHost');
    private resizeObserver?: ResizeObserver;

    readonly type = input<DashboardOpportunitiesGrid>('newProjects');

    readonly title = computed(() => (this.type() === 'newProjects' ? 'New Projects' : 'Upcoming Bids'));

    readonly dashboardOpportunities = signal<OpportunityView[]>([]);

    readonly columnDefs = computed<ColDef[]>(() => createDashboardOpportunitiesGridColumnDefs(this.type()));

    readonly noRows = computed(() => this.dashboardOpportunities().length === 0);

    readonly defaultColDef: ColDef = {
        sortable: false,
        resizable: false,
        suppressMovable: true,
        suppressHeaderMenuButton: true
    };

    ngOnInit(): void {
        void this.loadOpportunities();
    }

    ngAfterViewInit(): void {
        const host = this.gridHost()?.nativeElement;
        if (!host || typeof ResizeObserver === 'undefined') {
            return;
        }

        this.resizeObserver = new ResizeObserver(() => this.grid()?.sizeColumnsToFit());
        this.resizeObserver.observe(host);
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }

    private async loadOpportunities(): Promise<void> {
        const opportunities =
            this.type() === 'newProjects'
                ? await this.opportunityDashboardService.getNewProjects()
                : await this.opportunityDashboardService.getUpcomingBids();

        this.dashboardOpportunities.set(opportunities);
    }
}
